<!--
  Welcome — first-time landing for a Client (Store Owner) after the
  magic-link invitation accept flow (ADR 0050 / Phase F5).

  This page renders the brand-facing welcome message and three short
  orientation tiles. Customer-facing copy: matter-of-fact, descriptive,
  no demo / test framing.

  The page is reachable from /portal/welcome after the recipient sets
  their password. It is also routable directly (for example, the
  recipient bookmarks the URL); in that case the portal capability
  guard upstream ensures only Client-tier accounts can view it.

  Phase 2 will iterate on this surface as part of the full Client
  Portal redesign (see 2026-05-29 client-portal handoff). For F5 it
  ships as a clean, single-screen landing with a "Go to my store" CTA.
-->
<template>
  <v-container fluid class="welcome-page" data-testid="portal-welcome-page">
    <div class="welcome-card">
      <header class="welcome-header">
        <h1 class="welcome-title" data-testid="welcome-title">
          Welcome{{ greetingSuffix }}
        </h1>
        <p class="welcome-sub" data-testid="welcome-subtitle">
          {{ subtitle }}
        </p>
      </header>

      <ul class="welcome-tiles" data-testid="welcome-tiles">
        <li class="welcome-tile">
          <h2 class="welcome-tile__title">See order activity</h2>
          <p class="welcome-tile__body">
            Track every order placed through your store and check
            status from one place.
          </p>
        </li>
        <li class="welcome-tile">
          <h2 class="welcome-tile__title">Approve artwork</h2>
          <p class="welcome-tile__body">
            Review decoration proofs before they go to production and
            request changes when something needs to be different.
          </p>
        </li>
        <li class="welcome-tile">
          <h2 class="welcome-tile__title">Manage your team</h2>
          <p class="welcome-tile__body">
            Invite colleagues, set their access, and keep your
            store's account list up to date.
          </p>
        </li>
      </ul>

      <div class="welcome-actions">
        <button
          type="button"
          class="welcome-cta"
          data-testid="welcome-continue"
          @click="continueToPortal"
        >
          Go to my store
        </button>
      </div>
    </div>
  </v-container>
</template>

<script>
export default {
  name: 'PortalWelcome',
  computed: {
    account() {
      // Read the authed Account from Vuex if hydrated; otherwise leave
      // the greeting generic. The welcome page must NOT block on a
      // store hydrate -- the recipient just finished setting a
      // password and any 1-second loading wobble looks broken.
      return this.$store?.state?.account || null
    },
    distributorName() {
      const acct = this.account
      const dist = acct && acct.distributor
      if (!dist) return ''
      return dist.company_name || dist.name || ''
    },
    clientName() {
      const acct = this.account
      const client = acct && acct.client
      if (!client) return ''
      return client.name || ''
    },
    greetingSuffix() {
      const acct = this.account
      const first = acct && acct.first_name ? String(acct.first_name).trim() : ''
      return first ? `, ${first}` : ''
    },
    subtitle() {
      if (this.clientName && this.distributorName) {
        return `Your store for ${this.clientName} is set up. From here you can manage orders, approve artwork, and invite teammates. ${this.distributorName} is your distributor partner if you need help.`
      }
      if (this.clientName) {
        return `Your store for ${this.clientName} is set up. From here you can manage orders, approve artwork, and invite teammates.`
      }
      return 'Your store is set up. From here you can manage orders, approve artwork, and invite teammates.'
    },
  },
  methods: {
    continueToPortal() {
      // /portal is the canonical Client Portal landing. The portal
      // post-login router (in `router/groups/portal.js`) and the
      // requireToken redirect handle role-appropriate sub-routes.
      this.$router.push('/portal')
    },
  },
}
</script>

<style scoped>
.welcome-page {
  padding: 48px 16px;
  background: var(--tf-surface-alt, #f6f7fb);
  min-height: 100%;
}
.welcome-card {
  background: var(--tf-bg, #fff);
  max-width: 720px;
  margin: 0 auto;
  padding: 40px;
  border-radius: var(--tf-radius-sm, 8px);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
}
.welcome-header {
  margin-bottom: 32px;
}
.welcome-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 12px;
  color: var(--tf-fg-1, #111);
}
.welcome-sub {
  font-size: 15px;
  color: var(--tf-fg-2, #555);
  margin: 0;
  line-height: 1.5;
}
.welcome-tiles {
  list-style: none;
  padding: 0;
  margin: 0 0 32px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}
.welcome-tile {
  padding: 16px;
  border: 1px solid var(--tf-border, #d0d4dc);
  border-radius: var(--tf-radius-xs, 4px);
  background: var(--tf-bg, #fff);
}
.welcome-tile__title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 8px;
  color: var(--tf-fg-1, #111);
}
.welcome-tile__body {
  font-size: 13px;
  color: var(--tf-fg-2, #555);
  margin: 0;
  line-height: 1.4;
}
.welcome-actions {
  display: flex;
  justify-content: flex-end;
}
.welcome-cta {
  padding: 12px 24px;
  border: none;
  border-radius: var(--tf-radius-xs, 4px);
  background: var(--tf-accent, #2c5fff);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.welcome-cta:hover {
  filter: brightness(0.95);
}
</style>
